import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { type GridColDef, Pagination } from '@dhl/components'
import { PlusIcon } from '@dhl-ui/icons'
import { Box, Button, Grid, Paper } from '@dhl-ui/components'
import { useCountUsers, type User, useSearchUsers } from '@dhl/keycloak-user-api'
import { ExportAdminUsers } from '~/modules/adminusers/components'

import { links } from '~/AppRoutes'
import { AdminDataGrid, lowercaseTransform, SearchTextField, useSearchAndPage } from '~/common'

export const AdminUsersPage = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const {
    searchAndPageParams: { skip, max },
    handleSearch,
    page,
    pageSize,
    setPage,
    setPageSize,
    search,
  } = useSearchAndPage()

  const { data: userCount } = useCountUsers()
  const { data: users, isFetching: loading } = useSearchUsers({
    search,
    firstResult: skip,
    maxResult: max,
  })

  const handleRowClick = (user: User) => {
    const { id } = user
    const uri = links.userOverviewDetailsLink(id)
    navigate(uri)
  }
  const renderCellAsString = (value: string | undefined) => {
    return value ? value : ''
  }

  const columns: GridColDef[] = [
    {
      field: 'username',
      headerName: t('admin.adminUsersPage.userHeader.email'),
      valueGetter: renderCellAsString,
      flex: 2,
      sortable: false,
    },
    {
      field: 'firstName',
      headerName: t('admin.adminUsersPage.userHeader.firstName'),
      valueGetter: renderCellAsString,
      flex: 1,
      sortable: false,
    },
    {
      field: 'lastName',
      headerName: t('admin.adminUsersPage.userHeader.lastName'),
      valueGetter: renderCellAsString,
      flex: 1,
      sortable: false,
    },
    {
      field: 'enabled',
      headerName: t('admin.adminUsersPage.userHeader.enabled'),
      valueGetter: renderCellAsString,
      flex: 0.5,
      sortable: false,
    },
  ]
  return (
    <>
      <Grid container direction="row" justifyContent="space-between" alignItems="top" spacing={2}>
        <Grid>
          <SearchTextField onChange={handleSearch} debounceDelayMsec={600} transform={lowercaseTransform} />
        </Grid>
        <Grid container justifyContent="end" direction="row" gap={1}>
          <ExportAdminUsers />
          <Button
            data-testid="add-admin"
            color="primary"
            variant="contained"
            startIcon={<PlusIcon />}
            onClick={() => navigate(links.addFormLink())}
          >
            {t('admin.adminUsersPage.userHeader.addUser')}
          </Button>
        </Grid>
      </Grid>
      <Box mt={2}>
        <Paper>
          <AdminDataGrid
            loading={loading}
            data-testid="admins-table"
            autoHeight
            rows={users ?? []}
            columns={columns}
            onRowClick={({ row }) => handleRowClick(row)}
            rowHeight={48}
            columnHeaderHeight={48}
            disableRowSelectionOnClick
            disableColumnFilter
            hideFooter
          />
        </Paper>
        <Box mt={2}>
          <Pagination
            page={page}
            count={userCount ?? 0}
            onPageChange={(_, page) => setPage(page)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={setPageSize}
          />
        </Box>
      </Box>
    </>
  )
}
